import { getItem, removeItem, saveItem } from 'services/session-storage';

/**
 * Guards against an infinite app ↔ accounts redirect loop during startup.
 *
 * When the startup data fetch fails authorization (`InvalidSession`) the app clears the
 * session and redirects to accounts to re-authenticate. If accounts still has a live SSO
 * session it silently hands back a token that fails startup the same way, and the browser
 * ping-pongs forever — the UI never renders. See AC-3814.
 *
 * We count consecutive startup re-auth redirects within a short window; once the limit is
 * exceeded we stop redirecting and let the caller surface the startup-error page instead.
 * The counter is reset on any successful startup.
 */

const STORAGE_KEY = 'startup-reauth-attempts';

/** Consecutive re-auth redirects tolerated before we give up and show an error. */
const MAX_STARTUP_REAUTH_ATTEMPTS = 2;

/** Counter older than this (ms) is treated as stale and ignored — a fresh loop starts over. */
const ATTEMPT_WINDOW_MS = 60_000;

type ReauthAttempts = {
  count: number;
  timestamp: number;
};

/**
 * Records a startup re-auth attempt and reports whether another redirect to accounts is
 * still allowed. Returns `false` once the loop-protection limit is exceeded, signalling the
 * caller to show an error instead of redirecting again.
 */
export function registerStartupReauthAttempt(): boolean {
  const now = Date.now();
  const previous = getItem<ReauthAttempts>(STORAGE_KEY);
  const isWithinWindow = previous !== null && now - previous.timestamp < ATTEMPT_WINDOW_MS;
  const count = (isWithinWindow ? previous.count : 0) + 1;

  saveItem(STORAGE_KEY, { count, timestamp: now });

  return count <= MAX_STARTUP_REAUTH_ATTEMPTS;
}

/** Clears the re-auth counter. Call on a successful startup so the next real failure starts fresh. */
export function resetStartupReauthAttempts(): void {
  removeItem(STORAGE_KEY);
}
